import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import PhotoSignatureCapture from './PhotoSignatureCapture';
import SimpleCameraCapture from './SimpleCameraCapture';
import { FiCamera, FiCheck, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function SignaturePad({ value, onChange, placeholder, height = 'h-40 sm:h-48 lg:h-56', showPreview = true }) {
  const canvasRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [showSimpleCapture, setShowSimpleCapture] = useState(false);
  const [tempSignature, setTempSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setTempSignature(null);
      setIsEmpty(true);
      onChange('');
    }
  };

  const handleEnd = () => {
    if (canvasRef.current && !canvasRef.current.isEmpty()) {
      const dataURL = canvasRef.current.toDataURL('image/png');
      setTempSignature(dataURL);
      setIsEmpty(false);
      setIsDrawing(false);
    }
  };

  const handleBegin = () => {
    setIsEmpty(false);
    setIsDrawing(true);
  };

  const handleConfirmSignature = () => {
    if (tempSignature) {
      onChange(tempSignature);
      setTempSignature(null);
    }
  };

  const handlePhotoCapture = (imageData) => {
    console.log('📸 Imagen capturada desde cámara:', imageData ? 'Sí' : 'No');
    if (imageData) {
      onChange(imageData);
      setIsEmpty(false);
      setTempSignature(null);
    }
  };

  // Función para probar la cámara avanzada
  const tryAdvancedCamera = async () => {
    try {
      console.log('🔍 Verificando soporte de cámara avanzada...');
      // Verificación rápida de soporte
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('API no soportada');
      }
      // Test rápido de acceso con configuración ultra básica
      const testStream = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 } });
      // Si llegamos aquí, funciona - detener el test stream inmediatamente
      testStream.getTracks().forEach(track => track.stop());
      console.log('✅ Cámara avanzada disponible');
      setShowPhotoCapture(true);
    } catch (error) {
      console.log('❌ Cámara avanzada no disponible:', error.message);
      console.log('🔄 Cambiando a método simple');
      setShowSimpleCapture(true);
    }
  };

  // Función para el botón de cámara
  const handleCameraClick = () => {
    console.log('📱 Abriendo cámara');
    tryAdvancedCamera();
  };

  useEffect(() => {
    // Cargar la firma existente si hay un valor
    if (canvasRef.current && value && !tempSignature) {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getCanvas().getContext('2d');
          ctx.clearRect(0, 0, canvas.getCanvas().width, canvas.getCanvas().height);
          ctx.drawImage(img, 0, 0);
          setIsEmpty(false);
        }
      };
      img.src = value;
    }
  }, [value, tempSignature]);

  // Mostrar componente de cámara si está activo
  if (showPhotoCapture) {
    return (
      <PhotoSignatureCapture 
        value={value} 
        onChange={handlePhotoCapture} 
        onClose={() => {
          console.log('❌ Cerrando cámara avanzada');
          setShowPhotoCapture(false);
        }} 
      />
    );
  }

  if (showSimpleCapture) {
    return (
      <SimpleCameraCapture 
        onChange={handlePhotoCapture} 
        onClose={() => {
          console.log('❌ Cerrando cámara simple');
          setShowSimpleCapture(false);
        }} 
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="border-2 border-gray-300 rounded-lg bg-white relative">
        {/* Contenedor con aspecto cuadrado */}
        <div className="relative w-full" style={{ paddingBottom: '100%' }}>
          <div className="absolute inset-0">
            <SignatureCanvas
              ref={canvasRef}
              penColor="black"
              canvasProps={{
                className: `w-full h-full cursor-crosshair touch-none rounded-lg ${isDrawing ? 'cursor-crosshair' : ''}`,
                style: {
                  touchAction: 'none',
                  width: '100%',
                  height: '100%',
                  inputMode: 'none',
                  autoComplete: 'off',
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: 'false'
                }
              }}
              onEnd={handleEnd}
              onBegin={handleBegin}
              minWidth={0.5}
              maxWidth={2.5}
              throttle={16}
              backgroundColor="white"
              resizeCanvas={false}
            />
            {isEmpty && !value && !tempSignature && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center p-4">
                  <span className="text-gray-400 text-sm sm:text-base block">{placeholder}</span>
                  <span className="text-gray-300 text-xs mt-1 block">Dibuje su firma aquí</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Botones de acción */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          {/* Botón de cámara */}
          <button
            type="button"
            onClick={handleCameraClick}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors bg-blue-100 text-blue-600 hover:bg-blue-200"
            title="Capturar con cámara"
          >
            <SafeIcon icon={FiCamera} className="w-4 h-4" />
            <span className="hidden sm:inline">Cámara</span>
          </button>
          {/* Botón de limpiar */}
          {(value || tempSignature) && (
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-2 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
            >
              Limpiar
            </button>
          )}
          {/* Botón de confirmar (solo cuando hay firma temporal) */}
          {tempSignature && (
            <button
              type="button"
              onClick={handleConfirmSignature}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors bg-green-100 text-green-600 hover:bg-green-200"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
              Confirmar
            </button>
          )}
        </div>
        {/* Indicadores de estado - Eliminado el "Pendiente" */}
        <div className="flex items-center gap-2">
          {value && !tempSignature && (
            <span className="text-sm text-green-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Guardada
            </span>
          )}
        </div>
      </div>
      {/* Vista previa */}
      {showPreview && value && !tempSignature && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600 mb-2 font-medium">Vista previa:</p>
          <div className="bg-white p-2 rounded border border-gray-300">
            <div className="relative w-full" style={{ paddingBottom: '50%' }}>
              <div className="absolute inset-0">
                <img src={value} alt="Vista previa de la firma" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignaturePad;